import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  CircularProgress,
  Divider,
  Chip,
  useTheme,
  alpha,
  Card,
  CardContent,
  Tooltip
} from '@mui/material';
import { 
  Close as CloseIcon, 
  AccountBalance as AccountBalanceIcon,
  ArrowDownward as DebitIcon,
  ArrowUpward as CreditIcon,
  Info as InfoIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { getGLTransactions, GLTransaction, GLTransactionEntry } from '../firebase/glTransactionServices';
import { format } from 'date-fns';

interface GLTransactionViewProps {
  open: boolean;
  onClose: () => void;
  expenseId: string;
  expenseTitle: string;
}

const GLTransactionView: React.FC<GLTransactionViewProps> = ({ open, onClose, expenseId, expenseTitle }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState<GLTransaction[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTransactions = async () => {
      if (!open || !expenseId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const transactionData = await getGLTransactions(null, null, 'expense', expenseId);
        setTransactions(transactionData);
      } catch (err) {
        console.error('Error fetching GL transactions:', err);
        setError('Failed to load transaction details');
      } finally {
        setLoading(false);
      }
    };
    
    fetchTransactions();
  }, [open, expenseId]);

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: `1px solid ${theme.palette.divider}`,
        py: 3,
        px: 3,
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AccountBalanceIcon sx={{ fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            GL Transaction Entries
          </Typography>
        </Box>
        <IconButton 
          edge="end" 
          color="inherit" 
          onClick={onClose} 
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          mb: 3,
          pb: 2,
          borderBottom: `1px solid ${theme.palette.divider}`
        }}>
          <ReceiptIcon color="primary" fontSize="small" />
          <Typography variant="h6" color="primary">
            {expenseTitle}
          </Typography>
          <Chip 
            label="Expense" 
            size="small" 
            color="primary" 
            variant="outlined"
            sx={{ ml: 1 }}
          />
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ p: 2 }}>
            {error}
          </Typography>
        ) : transactions.length === 0 ? (
          <Box sx={{ 
            p: 4, 
            textAlign: 'center', 
            backgroundColor: alpha(theme.palette.warning.light, 0.1),
            borderRadius: 1
          }}>
            <InfoIcon color="warning" sx={{ fontSize: 40, mb: 2 }} />
            <Typography variant="subtitle1">
              No GL transactions found for this expense.
            </Typography>
          </Box>
        ) : (
          <>
            {transactions.map((transaction, index) => (
              <Card 
                key={transaction.id || index} 
                variant="outlined" 
                sx={{ 
                  mb: 3,
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: alpha(theme.palette.primary.light, 0.1),
                    borderBottom: `1px solid ${theme.palette.divider}`
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          icon={<AccountBalanceIcon fontSize="small" />}
                          label={format(
                            transaction.transactionDate instanceof Date 
                              ? transaction.transactionDate 
                              : transaction.transactionDate.toDate(), 
                            'dd MMM yyyy'
                          )}
                          size="small"
                          color="primary"
                          variant="filled"
                        />
                        <Tooltip title="Transaction ID">
                          <Chip
                            label={transaction.id ? transaction.id.substring(0, 8) : 'N/A'}
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Reference: {transaction.referenceType}/{transaction.referenceId}
                      </Typography>
                    </Box>
                    
                    <Typography variant="subtitle2" sx={{ mt: 1 }}>
                      {transaction.description}
                    </Typography>
                  </Box>
                  
                  <TableContainer sx={{ 
                    '& .MuiTableCell-root': { 
                      borderColor: theme.palette.divider,
                      py: 1.5
                    }
                  }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: alpha(theme.palette.grey[200], 0.5) }}>
                          <TableCell width="20%">Account Number</TableCell>
                          <TableCell width="40%">Account Name</TableCell>
                          <TableCell align="right" width="20%">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                              <DebitIcon fontSize="small" color="success" />
                              <Typography variant="subtitle2" color="success.main">Debit</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right" width="20%">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                              <CreditIcon fontSize="small" color="error" />
                              <Typography variant="subtitle2" color="error.main">Credit</Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {transaction.entries.map((entry: GLTransactionEntry, entryIndex: number) => (
                          <TableRow 
                            key={entryIndex}
                            sx={{ 
                              '&:nth-of-type(odd)': { backgroundColor: alpha(theme.palette.grey[100], 0.5) },
                              '&:hover': { backgroundColor: alpha(theme.palette.primary.light, 0.05) }
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {entry.accountNumber}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {entry.accountName}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography 
                                variant="body2" 
                                color={entry.debit > 0 ? 'success.main' : undefined}
                                fontWeight={entry.debit > 0 ? 'medium' : undefined}
                              >
                                {entry.debit > 0 ? formatCurrency(entry.debit) : '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography 
                                variant="body2" 
                                color={entry.credit > 0 ? 'error.main' : undefined}
                                fontWeight={entry.credit > 0 ? 'medium' : undefined}
                              >
                                {entry.credit > 0 ? formatCurrency(entry.credit) : '-'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow sx={{ 
                          backgroundColor: alpha(theme.palette.grey[200], 0.7),
                          borderTop: `2px solid ${theme.palette.divider}`
                        }}>
                          <TableCell colSpan={2} align="right">
                            <Typography variant="subtitle2" fontWeight="bold">Total</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="subtitle2" fontWeight="bold" color="success.main">
                              {formatCurrency(
                                transaction.entries.reduce((sum, entry) => sum + entry.debit, 0)
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="subtitle2" fontWeight="bold" color="error.main">
                              {formatCurrency(
                                transaction.entries.reduce((sum, entry) => sum + entry.credit, 0)
                              )}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            ))}
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ 
        px: 3, 
        py: 2, 
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: alpha(theme.palette.background.default, 0.5)
      }}>
        <Button 
          onClick={onClose} 
          variant="contained" 
          color="primary"
          startIcon={<CloseIcon />}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GLTransactionView;
